"use client";

/**
 * Minimap — renders the real world tileMap to a small canvas (2px per tile),
 * with live overlays for the player (gold), follower Surggies (cream), and
 * building markers (bronze). Pure/props-based: no store subscriptions here.
 *
 * Terrain is baked once per tileMap reference into an offscreen canvas;
 * per-render work is a single drawImage plus a handful of fillRects.
 *
 * All colors come from the PIXEL_STYLE.md 16-color master palette.
 */

import { useEffect, useRef } from "react";
import { TILE_SIZE, TileType } from "@/engine/constants";

const PX_PER_TILE = 2;

// ── PIXEL_STYLE.md palette ────────────────────────────────────────────────────
const INK = "#1a1a1a";
const CREAM = "#f5ecd6";
const GOLD = "#c5a83a";
const SAND = "#b89968";
const BRONZE = "#8a7a5c";
const WALNUT = "#5c4a30";
const MOSS = "#7a9a4a";
const FOREST = "#5c7935";
const DEEP_FOREST = "#3d5224";
const SHALLOW = "#a8d2e8";
const OCEAN = "#6a9bc4";
const DEEP = "#3d6890";

const TILE_MINI_COLORS: Record<number, string> = {
  [TileType.Empty]: INK,
  [TileType.Grass]: MOSS,
  [TileType.DarkGrass]: FOREST,
  [TileType.FlowerPatch]: MOSS,
  [TileType.TreeCanopy]: DEEP_FOREST,
  [TileType.PalmLeaves]: FOREST,
  [TileType.PalmTrunk]: WALNUT,
  [TileType.Sand]: SAND,
  [TileType.Path]: SAND,
  [TileType.Boardwalk]: BRONZE,
  [TileType.Ocean]: DEEP,
  [TileType.WaveAnimated]: OCEAN,
  [TileType.Pool]: SHALLOW,
  [TileType.BuildingExterior]: BRONZE,
  [TileType.BuildingFloor]: SAND,
  [TileType.Door]: WALNUT,
  [TileType.Wall]: WALNUT,
  [TileType.TikiBar]: WALNUT,
  [TileType.Rock]: BRONZE,
};

/** Unknown/future tile codes read as terrain rather than a hole. */
const FALLBACK_TILE_COLOR = FOREST;

type Vec2 = { x: number; y: number };

export interface MinimapProps {
  /** Tile map (rows of TileType codes). Takes precedence over getTileMap. */
  tileMap?: ReadonlyArray<ReadonlyArray<number>> | null;
  /** Lazy getter — re-polled each render until it yields a map (the overworld is registered async). */
  getTileMap?: () => ReadonlyArray<ReadonlyArray<number>> | null;
  /** Player position in world pixels. */
  playerPos: Vec2;
  /** Follower Surggies — world-pixel positions. */
  surggies?: ReadonlyArray<{ id: string; position?: Vec2 | null }>;
  /** Building markers — world-pixel positions. */
  buildings?: ReadonlyArray<{ id: string; x: number; y: number }>;
}

export function Minimap({ tileMap, getTileMap, playerPos, surggies, buildings }: MinimapProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const baseRef = useRef<{
    map: ReadonlyArray<ReadonlyArray<number>>;
    canvas: HTMLCanvasElement;
  } | null>(null);

  const map = tileMap ?? getTileMap?.() ?? null;
  const rows = map?.length ?? 0;
  const cols = rows > 0 ? (map?.[0]?.length ?? 0) : 0;
  // Fall back to overworld dims (80x60) so the frame holds shape while loading.
  const w = (cols > 0 ? cols : 80) * PX_PER_TILE;
  const h = (rows > 0 ? rows : 60) * PX_PER_TILE;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.imageSmoothingEnabled = false;

    ctx.fillStyle = INK;
    ctx.fillRect(0, 0, w, h);
    if (!map || rows === 0 || cols === 0) return;

    // Bake terrain once per tileMap reference.
    let base = baseRef.current;
    if (!base || base.map !== map) {
      const off = document.createElement("canvas");
      off.width = w;
      off.height = h;
      const octx = off.getContext("2d");
      if (!octx) return;
      for (let r = 0; r < rows; r++) {
        const rowArr = map[r];
        for (let c = 0; c < rowArr.length; c++) {
          octx.fillStyle = TILE_MINI_COLORS[rowArr[c]] ?? FALLBACK_TILE_COLOR;
          octx.fillRect(c * PX_PER_TILE, r * PX_PER_TILE, PX_PER_TILE, PX_PER_TILE);
        }
      }
      base = { map, canvas: off };
      baseRef.current = base;
    }
    ctx.drawImage(base.canvas, 0, 0);

    const worldW = cols * TILE_SIZE;
    const worldH = rows * TILE_SIZE;

    // Hard-edged square marker with a 1px ink backing — no circles, no AA.
    const stamp = (wx: number, wy: number, size: number, color: string) => {
      const cx = Math.max(0, Math.min(w - 1, Math.round((wx / worldW) * w)));
      const cy = Math.max(0, Math.min(h - 1, Math.round((wy / worldH) * h)));
      const half = Math.floor(size / 2);
      ctx.fillStyle = INK;
      ctx.fillRect(cx - half - 1, cy - half - 1, size + 2, size + 2);
      ctx.fillStyle = color;
      ctx.fillRect(cx - half, cy - half, size, size);
    };

    for (const b of buildings ?? []) stamp(b.x, b.y, 3, BRONZE);
    for (const s of surggies ?? []) {
      if (s.position) stamp(s.position.x, s.position.y, 2, CREAM);
    }
    stamp(playerPos.x, playerPos.y, 3, GOLD);
  }, [map, rows, cols, w, h, playerPos, surggies, buildings]);

  return (
    <div className="absolute bottom-4 right-4 z-40" data-testid="minimap">
      {/* Panel frame per PIXEL_STYLE: 2px ink outline + cream top-left inner highlight. */}
      <div style={{ border: `2px solid ${INK}`, backgroundColor: INK, lineHeight: 0 }}>
        <canvas
          ref={canvasRef}
          width={w}
          height={h}
          style={{
            display: "block",
            imageRendering: "pixelated",
            borderTop: "1px solid rgba(245, 236, 214, 0.4)",
            borderLeft: "1px solid rgba(245, 236, 214, 0.4)",
            borderBottom: "1px solid rgba(92, 74, 48, 0.6)",
            borderRight: "1px solid rgba(92, 74, 48, 0.6)",
          }}
        />
      </div>
    </div>
  );
}

export default Minimap;
